import { CalendarDate, parseDate } from "@internationalized/date";
import {
  isoWeekday,
  isSchooldag,
  type SchooljaarRange,
} from "#/modules/les.planning/domain/calendar";
import { regelAppliesOn, type Roosterregel } from "#/modules/les.planning/domain/rooster";
import type { components } from "#/schema";

export type Vakantieregio = components["schemas"]["Vakantieregio"];
/** A schoolvakantie of the regio: the school's own dates and the Rijksoverheid's advice. */
export type Vakantie = components["schemas"]["VakantieModel"];
export type Feestdag = components["schemas"]["FeestdagModel"];
/** A day, or days in a row, the school itself has no gym on, such as a studiedag. */
export type VrijeDag = components["schemas"]["VrijeDagModel"];

/** Everything the school has no gym on in the schooljaar, and the regio that decides most of it. */
export interface VakantiesOverzicht {
  vakantieregio: Vakantieregio | null;
  vakanties: Vakantie[];
  feestdagen: Feestdag[];
  vrijeDagen: VrijeDag[];
}

/** Days without gym in a row, both ends included, as ISO dates. */
export type VrijePeriode = Pick<Vakantie, "naam" | "start" | "eind">;

export const VAKANTIEREGIOS: readonly Vakantieregio[] = ["Noord", "Midden", "Zuid"];

export const MAX_NAAM_LENGTH = 100;

export const EMPTY_VAKANTIES: VakantiesOverzicht = {
  vakantieregio: null,
  vakanties: [],
  feestdagen: [],
  vrijeDagen: [],
};

const SEPTEMBER = 9;
const DAY_MS = 86_400_000;

const byStart = (a: VrijePeriode, b: VrijePeriode) =>
  a.start.localeCompare(b.start) || a.naam.localeCompare(b.naam, "nl");

const contains = (periode: VrijePeriode, day: string) =>
  periode.start <= day && periode.eind >= day;

const getDays = (start: CalendarDate, eind: CalendarDate): CalendarDate[] => {
  const days: CalendarDate[] = [];
  for (let day = start; day.compare(eind) <= 0; day = day.add({ days: 1 })) days.push(day);
  return days;
};

/**
 * Every vrije periode of the overview by start: the vakanties, the feestdagen and the eigen vrije
 * dagen, without the vrije dag that is being changed.
 */
export const getVrijePeriodes = (
  overzicht: VakantiesOverzicht,
  exceptVrijeDagId?: string,
): VrijePeriode[] =>
  [
    ...overzicht.vakanties,
    ...overzicht.feestdagen.map(({ naam, datum }) => ({ naam, start: datum, eind: datum })),
    ...overzicht.vrijeDagen.filter((vrijeDag) => vrijeDag.id !== exceptVrijeDagId),
  ].sort(byStart);

/** The earliest-starting vrije periode that shares a day with start up to and including eind. */
export const findOverlap = (
  periodes: readonly VrijePeriode[],
  start: CalendarDate,
  eind: CalendarDate,
): VrijePeriode | null =>
  periodes.find(
    (periode) => periode.start <= eind.toString() && periode.eind >= start.toString(),
  ) ?? null;

/** The feestdagen that take a schooldag: not at the weekend and not in a vakantie. */
export const getSchoolFeestdagen = (
  feestdagen: readonly Feestdag[],
  vakanties: readonly Vakantie[],
): CalendarDate[] =>
  feestdagen
    .filter(
      ({ datum }) =>
        isSchooldag(parseDate(datum)) && !vakanties.some((vakantie) => contains(vakantie, datum)),
    )
    .map(({ datum }) => parseDate(datum))
    .sort((a, b) => a.compare(b));

/** How many lessen of the rooster fall on the schooldagen from start to eind that are not vrij yet. */
export const countRoosterLessen = (
  regels: readonly Roosterregel[],
  start: CalendarDate,
  eind: CalendarDate,
  vrij: readonly VrijePeriode[],
): number =>
  getDays(start, eind)
    .filter((day) => isSchooldag(day) && !vrij.some((periode) => contains(periode, day.toString())))
    .reduce(
      (aantal, day) => aantal + regels.filter((regel) => regelAppliesOn(regel, day)).length,
      0,
    );

/** One day, the five schooldagen of one week, or any other run of days. */
export type Periodeduur = "dag" | "week" | "dagen";

export const getPeriodeduur = (start: CalendarDate, eind: CalendarDate): Periodeduur => {
  if (start.compare(eind) === 0) return "dag";
  const schooldagen = getDays(start, eind).filter(isSchooldag);
  const [first] = schooldagen;
  const last = schooldagen.at(-1);
  const isWeek =
    schooldagen.length === 5 &&
    first !== undefined &&
    last !== undefined &&
    isoWeekday(first) === 1 &&
    daysBetween(first, last) === 4;
  return isWeek ? "week" : "dagen";
};

const daysBetween = (from: CalendarDate, to: CalendarDate) =>
  Math.round(
    (Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) /
      DAY_MS,
  );

/** The days a date may be on, both included. */
export interface DateRange {
  readonly min: CalendarDate;
  readonly max: CalendarDate;
}

/** Vrije dagen lie in the schooljaar, a vakantie starts in it: Aug 1 – Jul 31. */
export const getSchooljaarDays = (range: SchooljaarRange): DateRange => ({
  min: range.start,
  max: range.end,
});

/** A vakantie may end after the schooljaar: the zomervakantie runs on into September. */
export const getVakantieEindDays = (range: SchooljaarRange): DateRange => ({
  min: range.start,
  max: new CalendarDate(range.fromYear + 1, SEPTEMBER, 30),
});

/** What is wrong with a date of a period. */
export type DateError =
  | { readonly kind: "outOfRange"; readonly range: DateRange }
  | { readonly kind: "beforeStart"; readonly start: CalendarDate }
  | { readonly kind: "overlap"; readonly naam: string };

const isInRange = (day: CalendarDate, range: DateRange) =>
  day.compare(range.min) >= 0 && day.compare(range.max) <= 0;

const checkStart = (start: CalendarDate | null, range: DateRange): DateError | null =>
  start && !isInRange(start, range) ? { kind: "outOfRange", range } : null;

const checkEind = (
  start: CalendarDate | null,
  eind: CalendarDate | null,
  range: DateRange,
): DateError | null => {
  if (!eind) return null;
  if (start && eind.compare(start) < 0) return { kind: "beforeStart", start };
  return isInRange(eind, range) ? null : { kind: "outOfRange", range };
};

/** A checked period: an error per date, and the period itself once it can be saved. */
export interface PeriodCheck {
  readonly start: DateError | null;
  readonly eind: DateError | null;
  readonly period: { readonly start: CalendarDate; readonly eind: CalendarDate } | null;
}

/** New dates for a vakantie, checked as the backend does. */
export const checkVakantie = (
  start: CalendarDate | null,
  eind: CalendarDate | null,
  range: SchooljaarRange,
): PeriodCheck => {
  const startError = checkStart(start, getSchooljaarDays(range));
  const eindError = checkEind(start, eind, getVakantieEindDays(range));
  return {
    start: startError,
    eind: eindError,
    period: start && eind && !startError && !eindError ? { start, eind } : null,
  };
};

/**
 * The dates of a vrije dag, checked as the backend does; without an end it is one day. The overlap
 * shows at the date that lies in the other vrije periode.
 */
export const checkVrijeDag = (
  start: CalendarDate | null,
  eind: CalendarDate | null,
  range: SchooljaarRange,
  periodes: readonly VrijePeriode[],
): PeriodCheck => {
  const days = getSchooljaarDays(range);
  const startError = checkStart(start, days);
  const eindError = checkEind(start, eind, days);
  if (!start || startError || eindError)
    return { start: startError, eind: eindError, period: null };
  const last = eind ?? start;
  const overlap = findOverlap(periodes, start, last);
  if (!overlap) return { start: null, eind: null, period: { start, eind: last } };
  const error: DateError = { kind: "overlap", naam: overlap.naam };
  return contains(overlap, start.toString())
    ? { start: error, eind: null, period: null }
    : { start: null, eind: error, period: null };
};

export const isAangepast = (vakantie: Vakantie, start: string, eind: string) =>
  start !== vakantie.adviesStart || eind !== vakantie.adviesEind;

export const withVakantieregio = (
  overzicht: VakantiesOverzicht,
  vakantieregio: Vakantieregio,
): VakantiesOverzicht => ({ ...overzicht, vakantieregio });

export const withVakantie = (
  overzicht: VakantiesOverzicht,
  naam: string,
  dates: Pick<Vakantie, "start" | "eind">,
): VakantiesOverzicht => ({
  ...overzicht,
  vakanties: overzicht.vakanties
    .map((vakantie) =>
      vakantie.naam === naam
        ? { ...vakantie, ...dates, aangepast: isAangepast(vakantie, dates.start, dates.eind) }
        : vakantie,
    )
    .sort(byStart),
});

export const withVrijeDag = (
  overzicht: VakantiesOverzicht,
  vrijeDag: VrijeDag,
): VakantiesOverzicht => ({
  ...overzicht,
  vrijeDagen: [...overzicht.vrijeDagen.filter(({ id }) => id !== vrijeDag.id), vrijeDag].sort(
    byStart,
  ),
});

export const withoutVrijeDag = (overzicht: VakantiesOverzicht, id: string): VakantiesOverzicht => ({
  ...overzicht,
  vrijeDagen: overzicht.vrijeDagen.filter((vrijeDag) => vrijeDag.id !== id),
});

const monthFormatter = new Intl.DateTimeFormat("nl-NL", { month: "short", timeZone: "UTC" });
const weekdayFormatter = new Intl.DateTimeFormat("nl-NL", { weekday: "short", timeZone: "UTC" });
const listFormatter = new Intl.ListFormat("nl-NL", { type: "conjunction" });

const toUtc = (day: CalendarDate) => Date.UTC(day.year, day.month - 1, day.day);
// Some ICU versions end the abbreviations with a full stop ("okt."); the planning writes them without.
const month = (day: CalendarDate) => monthFormatter.format(toUtc(day)).replace(".", "");
const weekday = (day: CalendarDate) => weekdayFormatter.format(toUtc(day)).replace(".", "");

/** "30 okt 2026" */
export const formatDate = (day: CalendarDate) => `${day.day} ${month(day)} ${day.year}`;

/** "17 – 25 okt 2026", "24 apr – 2 mei 2027" or "19 dec 2026 – 3 jan 2027"; one day as a date. */
export const formatDateRange = (start: CalendarDate, eind: CalendarDate) => {
  if (start.compare(eind) === 0) return formatDate(start);
  if (start.year !== eind.year) return `${formatDate(start)} – ${formatDate(eind)}`;
  const from = start.month === eind.month ? `${start.day}` : `${start.day} ${month(start)}`;
  return `${from} – ${formatDate(eind)}`;
};

/** A vrije dag with its weekday, "vr 30 okt 2026"; days in a row as their range. */
export const formatVrijeDag = (start: CalendarDate, eind: CalendarDate) =>
  start.compare(eind) === 0
    ? `${weekday(start)} ${formatDate(start)}`
    : formatDateRange(start, eind);

const formatShortDate = (day: CalendarDate) => `${day.day} ${month(day)}`;

/** Without the year, which the schooljaar already says: "17 – 25 okt", "19 dec – 3 jan". */
export const formatShortDateRange = (start: CalendarDate, eind: CalendarDate) => {
  if (start.compare(eind) === 0) return formatShortDate(start);
  const sameMonth = start.month === eind.month && start.year === eind.year;
  return `${sameMonth ? start.day : formatShortDate(start)} – ${formatShortDate(eind)}`;
};

/** `formatVrijeDag` without the year: "vr 30 okt". */
export const formatShortVrijeDag = (start: CalendarDate, eind: CalendarDate) =>
  start.compare(eind) === 0
    ? `${weekday(start)} ${formatShortDate(start)}`
    : formatShortDateRange(start, eind);

/** "29 mrt, 6 mei en 17 mei 2027": the year once, after the last day in it. */
export const formatDays = (days: readonly CalendarDate[]) =>
  listFormatter.format(
    days.map((day, index) =>
      days[index + 1]?.year === day.year ? `${day.day} ${month(day)}` : formatDate(day),
    ),
  );
